import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext.jsx'
import { Eye, EyeOff, ArrowRight, AlertCircle, Shield } from 'lucide-react'
import './Login.css'

function Login() {
  const navigate = useNavigate()
  const { login, completeTwoFactor, isAuthenticated, isLoading } = useAuth()
  const [formData, setFormData] = useState({ email: '', password: '', remember: false })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [mfaToken, setMfaToken] = useState(null)
  const [mfaCode, setMfaCode] = useState('')

  if (isAuthenticated && !isLoading) return <Navigate to="/dashboard" replace />

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    try {
      const result = await login(formData.email, formData.password, formData.remember)

      if (result.requires_2fa) {
        setMfaToken(result.mfa_token)
      } else if (result.success) {
        navigate('/dashboard')
      } else {
        setError(result.error || 'Invalid email or password')
      }
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail)
      } else if (err.response?.data?.message) {
        setError(err.response.data.message)
      } else {
        setError('Invalid email or password')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleMfaSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')
    try {
      const result = await completeTwoFactor(mfaToken, mfaCode.trim(), formData.remember)
      if (result.success) {
        navigate('/dashboard')
      } else {
        setError(result.error || 'Invalid code')
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-bg"><div className="bg-gradient"></div><div className="bg-grid"></div><div className="bg-glow"></div></div>
      <div className="login-container">
        <div className="login-branding">
          <div className="branding-content">
            <div className="branding-logo"><img src="/logo_black.png" alt="Synxreach" className="branding-logo-img" /></div>
            <h1 className="branding-headline">AI-Powered Email Marketing That Actually Works</h1>
            <p className="branding-subtitle">Send smarter campaigns, reach more inboxes, and grow your business with intelligent automation.</p>
            <div className="branding-features">
              <div className="feature"><span className="feature-icon">99.2%</span><span>Deliverability</span></div>
              <div className="feature"><span className="feature-icon">AI</span><span>Co-Pilot</span></div>
              <div className="feature"><span className="feature-icon">Live</span><span>Analytics</span></div>
            </div>
          </div>
        </div>
        <div className="login-form-section">
          <div className="login-form-container">
            {mfaToken ? (
              <>
                <div className="login-header">
                  <h2><Shield size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />Two-factor authentication</h2>
                  <p>Enter the 6-digit code from your authenticator app (or a backup code)</p>
                </div>
                <form onSubmit={handleMfaSubmit} className="login-form">
                  {error && <div className="error-message"><AlertCircle size={18} /><span>{error}</span></div>}
                  <div className="form-group">
                    <label htmlFor="mfa">Authentication code</label>
                    <div className="input-wrapper">
                      <input type="text" id="mfa" inputMode="numeric" maxLength={10} autoFocus
                        value={mfaCode} onChange={e => setMfaCode(e.target.value)} placeholder="123456" required />
                    </div>
                  </div>
                  <button type="submit" className="login-btn" disabled={isSubmitting || !mfaCode.trim()}>
                    {isSubmitting ? <span className="btn-loading"></span> : <><span>Verify</span><ArrowRight size={18} /></>}
                  </button>
                  <button type="button" className="forgot-link" style={{ background: 'none', border: 0, marginTop: 8 }}
                    onClick={() => { setMfaToken(null); setMfaCode(''); setError('') }}>← Back to login</button>
                </form>
              </>
            ) : (
              <>
                <div className="login-header"><h2>Welcome back</h2><p>Sign in to your account to continue</p></div>
                <form onSubmit={handleSubmit} className="login-form">
                  {error && <div className="error-message"><AlertCircle size={18} /><span>{error}</span></div>}
                  <div className="form-group">
                    <label htmlFor="email">Email address</label>
                    <div className="input-wrapper"><input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@company.com" required autoComplete="email" /></div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <div className="input-wrapper">
                      <input type={showPassword ? 'text' : 'password'} id="password" name="password" value={formData.password} onChange={handleChange} placeholder="Enter your password" required autoComplete="current-password" />
                      <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                    </div>
                  </div>
                  <div className="form-options">
                    <label className="checkbox-label"><input type="checkbox" name="remember" checked={formData.remember} onChange={handleChange} /><span className="checkbox-custom"></span><span>Remember me</span></label>
                    <a href="#forgot" className="forgot-link">Forgot password?</a>
                  </div>
                  <button type="submit" className="login-btn" disabled={isSubmitting}>{isSubmitting ? <span className="btn-loading"></span> : <><span>Sign in</span><ArrowRight size={18} /></>}</button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
